/**
 * Dashboard Page (Main Map)
 * Pure project selector — no inventory, no filters, just TopAppBar + project picker.
 * Users select a project to enter full project view with all UI.
 */

import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { WebMercatorViewport } from '@deck.gl/core';

import AppBar from '../elements/AppBar';
import AsseTracMap from '../elements/maps/AsseTracMap';
import ProjectYardSelector from '../elements/ProjectYardSelector';

import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setProjectBoundariesData, setOverlaysData, setMovesData, clearSavedProjectState, setVisibleOverlays, updateViewport, setAssetsData } from '../../store/slices/mapSlice';
import { setCurrentProject } from '../../store/slices/projectSlice';
import { fetchProjectsByCustomer } from '../../store/slices/projectSlice';

const DashboardPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const mountedRef = useRef(false);

  const { projectList } = useAppSelector((state) => state.project);
  const { activeCustomer } = useAppSelector((state) => state.customer);
  // ASS-86: AsseTracMap dispatches updateViewport({ width, height }) on its
  // first resize, so these land in state.map.viewport once the map has
  // mounted. Needed for a real fitBounds calculation below.
  const mapWidth = useAppSelector((state) => state.map.viewport.width);
  const mapHeight = useAppSelector((state) => state.map.viewport.height);

  // Load projects for the customer
  useEffect(() => {
    if (activeCustomer?.id) {
      dispatch(fetchProjectsByCustomer(activeCustomer.id));
    }
    mountedRef.current = true;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mountedRef.current && activeCustomer?.id) {
      dispatch(fetchProjectsByCustomer(activeCustomer.id));
    }
  }, [activeCustomer?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear stale map overlays, moves, visible overlay state, assets, and current project when entering the dashboard
  // This ensures the Projects dropdown defaults to "Projects" placeholder and no project-specific assets are shown
  useEffect(() => {
    dispatch(setOverlaysData({}));
    dispatch(setMovesData({}));
    dispatch(setVisibleOverlays([]));
    dispatch(clearSavedProjectState());
    dispatch(setAssetsData({}));
    dispatch(setCurrentProject(null));
  }, [dispatch]);

  // ASS-86: fallback zoom-by-span table, used only when the map hasn't
  // resized yet (width/height still 0 on the very first load) so
  // WebMercatorViewport.fitBounds has nothing to compute against. Extended
  // range vs. the old table -- the old table topped out at "maxDelta > 2 =>
  // zoom 7", which is why an 11.6-degree span (a project in Oklahoma
  // alongside Minnesota ones) still got a zoom that couldn't fit it: every
  // span over 2 degrees collapsed to the same zoom 7 (then -1 to 6),
  // regardless of whether the true span was 2.1 degrees or 40.
  const calculateFallbackZoomLevel = (latDelta, lngDelta) => {
    const maxDelta = Math.max(latDelta, lngDelta);
    let zoom;
    if (maxDelta > 20) zoom = 3;
    else if (maxDelta > 10) zoom = 4;
    else if (maxDelta > 5) zoom = 5;
    else if (maxDelta > 2) zoom = 6;
    else if (maxDelta > 1) zoom = 8;
    else if (maxDelta > 0.5) zoom = 9;
    else if (maxDelta > 0.2) zoom = 10;
    else if (maxDelta > 0.1) zoom = 11;
    else zoom = 12;
    // Capped at 12 to match the fitBounds path's cap -- "a single point or
    // project gets a sensible close view," not a street-level zoom. The
    // old table's finer-grained tail (13/14/15 for maxDelta below 0.05/
    // 0.02/0.01) is folded into this same cap.
    return Math.min(Math.max(zoom, 2), 12);
  };

  // R-54 (ASS-63): parse a project's `boundaries` field into a list of
  // [lng, lat] pairs regardless of which shape it arrives in. The wire has
  // been in three shapes concurrently for a long time and nothing said so:
  //   (a) GeoJSON Polygon STRING       {"type":"Polygon","coordinates":[[[lng,lat,0],…]]}
  //   (b) Array of {latitude, longitude}   (legacy fixture shape)
  //   (c) null / undefined / anything else (an unmapped project)
  //
  // useMapLayers.js:129-149 already handles (a) and (b) when reading from the
  // store; DashboardPage's effect was accepting (b) only and silently skipping
  // (a), which meant every wizard-written boundary vanished before it reached
  // the store. This helper mirrors useMapLayers' shape handling so the two
  // consumers agree.
  //
  // Returns [{ lat, lng }, ...] on success; [] on unknown shape (and logs).
  // Does NOT settle the canonical shape — that decision spans the API, the
  // wizard, R-21, and this reader and is not this ticket's to make.
  const parseBoundaryCoords = (project) => {
    if (project.boundaries == null) return [];
    if (Array.isArray(project.boundaries)) {
      return project.boundaries
        .filter((c) => c && c.latitude != null && c.longitude != null)
        .map((c) => ({ lat: c.latitude, lng: c.longitude }));
    }
    if (typeof project.boundaries === 'string') {
      try {
        const geo = JSON.parse(project.boundaries);
        if (geo.type === 'Polygon' && Array.isArray(geo.coordinates?.[0])) {
          return geo.coordinates[0]
            .filter((c) => Array.isArray(c) && c.length >= 2)
            .map((c) => ({ lng: c[0], lat: c[1] }));
        }
        // Known GeoJSON envelope but not a shape we handle here.
        // eslint-disable-next-line no-console
        console.warn(
          '[R-54] project.boundaries is a GeoJSON string but not a Polygon:',
          { projectId: project.id, geoType: geo?.type },
        );
        return [];
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(
          '[R-54] project.boundaries string is not valid JSON:',
          { projectId: project.id, error: e.message },
        );
        return [];
      }
    }
    // Unknown shape. Log loudly — the operator's ASS-63 finding was that a
    // shape mismatch produced an empty map with no warning; this makes the
    // next mismatch discoverable in five seconds via the console.
    // eslint-disable-next-line no-console
    console.warn(
      '[R-54] project.boundaries has an unknown shape:',
      { projectId: project.id, type: typeof project.boundaries },
    );
    return [];
  };

  // Update map project boundaries and zoom to fit
  useEffect(() => {
    // R-54 note: the status filter is case-sensitive-lowercase ('active').
    // A capitalisation drift on the project side (Customer records use
    // 'Active' capitalised; a wire that returns 'Active' for projects would
    // land here) would silently empty the map — same failure class as the
    // customer-switcher whitelist. We do not widen the filter here; instead,
    // we count what got dropped so the console.warn can distinguish 'empty
    // because of status filter' from 'empty because of shape filter'.
    const activeProjects = projectList.filter(p => p.status === 'active');
    const droppedForStatus = projectList.length - activeProjects.length;
    let droppedForShape = 0;

    const projectBoundariesData = {};
    let minLat = Infinity, maxLat = -Infinity;
    let minLng = Infinity, maxLng = -Infinity;

    for (const project of activeProjects) {
      const coords = project.boundaries == null ? [] : parseBoundaryCoords(project);

      if (coords.length > 0) {
        // Boundary-rendered: has a real polygon shape. Beacon and Both
        // projects with real boundaries take this path exactly as before --
        // this branch is untouched from the pre-ASS-81 code.
        projectBoundariesData[project.id] = {
          id: project.id,
          boundaries: project.boundaries,
          name: project.name,
          client: project.client,
          status: project.status,
        };
        for (const { lat, lng } of coords) {
          minLat = Math.min(minLat, lat);
          maxLat = Math.max(maxLat, lat);
          minLng = Math.min(minLng, lng);
          maxLng = Math.max(maxLng, lng);
        }
        continue;
      }

      // ASS-81 Part A: no parseable boundary, but a manual location
      // (lat/lng) exists -- point-rendered instead of boundary-rendered.
      // A project is EITHER boundary-rendered OR point-rendered, never both
      // (this branch only runs when coords.length === 0 above).
      const lat = project.location?.lat;
      const lng = project.location?.lng;
      const hasValidPoint = typeof lat === 'number' && typeof lng === 'number'
        && !Number.isNaN(lat) && !Number.isNaN(lng);

      if (hasValidPoint) {
        projectBoundariesData[project.id] = {
          id: project.id,
          name: project.name,
          client: project.client,
          status: project.status,
          // No `boundaries` key -- useMapLayers.js's boundary-path derivation
          // (parseBoundaryCoords via the `path` field) skips entries with no
          // parseable path, so this entry never enters the polygon/path
          // layers. `location` is what marks it point-only for the marker
          // layer to pick up.
          location: { lat, lng },
        };
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
        minLng = Math.min(minLng, lng);
        maxLng = Math.max(maxLng, lng);
        continue;
      }

      if (project.boundaries != null) {
        // Had a boundaries field, but it didn't parse -- the pre-existing
        // shape-mismatch case.
        droppedForShape += 1;
      }
      // Else: no boundary and no location -- genuinely unmapped (R-22
      // draft-status case). Not counted in droppedForShape since it was
      // never expected to have a shape.
    }

    // R-54 loud-failure: if the map has zero drawable projects, say why.
    // Distinguishes 'no active projects' (upstream) from 'projects exist but
    // no boundaries parsed' (shape mismatch). Silent when the map has
    // something to draw.
    if (Object.keys(projectBoundariesData).length === 0 && projectList.length > 0) {
      // eslint-disable-next-line no-console
      console.warn(
        '[R-54] Dashboard map has no drawable projects.',
        {
          total: projectList.length,
          droppedForStatus,
          droppedForShape,
          statusesSeen: [...new Set(projectList.map(p => p.status))],
        },
      );
    }

    dispatch(setProjectBoundariesData(projectBoundariesData));

    if (minLat !== Infinity && Object.keys(projectBoundariesData).length > 0) {
      const centerLat = (minLat + maxLat) / 2;
      const centerLng = (minLng + maxLng) / 2;
      const latSpan = maxLat - minLat;
      const lngSpan = maxLng - minLng;

      // ASS-86: real fit-to-bounds via WebMercatorViewport, using the map's
      // actual pixel dimensions (set by AsseTracMap on its first resize).
      // The old calculateZoomLevel/Math.max(zoom-1,4) scheme collapsed
      // every span over 2 degrees to the same zoom, which is exactly why
      // an 11.6-degree span (a stray project far from the rest) opened
      // with zero pins in view: the fixed zoom couldn't fit it, no matter
      // how far apart the pins actually were.
      if (mapWidth && mapHeight) {
        try {
          const viewport = new WebMercatorViewport({ width: mapWidth, height: mapHeight });
          const { longitude, latitude, zoom } = viewport.fitBounds(
            [[minLng, minLat], [maxLng, maxLat]],
            { padding: 80 }
          );
          // Cap/floor: maxZoom 12 covers a single point/project sensibly
          // (fitBounds on a zero-span box can return a very high zoom);
          // minZoom 2 avoids over-zooming out past a sane world view.
          const clampedZoom = Math.min(Math.max(zoom, 2), 12);
          dispatch(updateViewport({ latitude, longitude, zoom: clampedZoom }));
        } catch (err) {
          // fitBounds can throw on degenerate input (e.g. NaN bounds) --
          // fall through to the fallback table rather than leave the
          // viewport unset.
          // eslint-disable-next-line no-console
          console.warn('[ASS-86] fitBounds failed, using fallback zoom table.', err);
          const zoom = calculateFallbackZoomLevel(latSpan, lngSpan);
          dispatch(updateViewport({ latitude: centerLat, longitude: centerLng, zoom }));
        }
      } else {
        // Map hasn't resized yet (width/height still 0) -- use the
        // extended fallback table rather than block on the map's own
        // resize effect.
        const zoom = calculateFallbackZoomLevel(latSpan, lngSpan);
        dispatch(updateViewport({ latitude: centerLat, longitude: centerLng, zoom }));
      }
    }
  }, [dispatch, projectList, mapWidth, mapHeight]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <AppBar
        showProjectSelector
        projectSelectorProps={{
          ProjectSelectorComponent: (
            <ProjectYardSelector
              projects={projectList}
              selectedProjectId={null /* AST-125 PS-5: the global map reads "Projects" */}
              onProjectSelect={(projectId) => {
                if (projectId) {
                  // Navigate to the selected project
                  navigate(`/projects/${projectId}`);
                } else {
                  // AST-125 PS-3: "Projects" opens Home.
                  dispatch(setCurrentProject(null));
                  navigate('/');
                }
              }}
              placeholder="Projects"
            />
          ),
        }}
      />

      {/* Map -- shows project boundaries only */}
      <Box sx={{ flexGrow: 1, position: 'relative' }}>
        <AsseTracMap />
      </Box>
    </Box>
  );
};

export default DashboardPage;
